// ─────────────────────────────────────────────────────────────
// ANALYTICS ENGINE
// Pure function — computes derived metrics from an array of trades.
//
// P&L convention:
//   - `t.pnl` stored on each trade is GROSS (matches broker statement)
//   - Net P&L (what actually moves the account balance) = pnl - fees - commissions
//   - We expose BOTH `totalPnl` (gross, for headline P&L) and `netPnl`
//     (for account balance). Equity curve uses NET so the line ends
//     at the broker's actual balance.
//
// Win-rate convention (locked in 2026-05-17):
//   - Win/Loss is determined by NET P&L (not gross, not the `result` text field)
//   - Scratch trades (net P&L === 0) are EXCLUDED from the denominator
//   - Open trades (is_closed === false) are EXCLUDED entirely
//   - winRate = wins / (wins + losses)
//   - Returns null when no closed non-scratch trades exist (caller renders "—")
// ─────────────────────────────────────────────────────────────

// Net P&L for a single trade (gross - fees - commissions).
// Used by classifyTrade and as a building block elsewhere.
export function tradeNet(t) {
  const gross = Number(t.pnl)         || 0;
  const fees  = Number(t.fees)        || 0;
  const comm  = Number(t.commissions) || 0;
  return gross - fees - comm;
}

// Classifies a single trade. Single source of truth for win/loss/scratch/open.
// Use this everywhere instead of reading t.result directly.
//   - 'open'    = is_closed === false (not yet realized)
//   - 'win'     = net P&L > 0
//   - 'loss'    = net P&L < 0
//   - 'scratch' = net P&L === 0 (excluded from win rate denominator)
export function classifyTrade(trade) {
  if (trade?.is_closed === false) return "open";
  const net = tradeNet(trade);
  if (net > 0) return "win";
  if (net < 0) return "loss";
  return "scratch";
}

// Canonical win rate calculation. Use this everywhere in the app.
// Returns an object so callers can use the breakdown when needed.
//   - winRate: wins / (wins + losses), or null if denominator is 0
//   - wins, losses, scratches, openTrades: raw counts
//   - denominator: wins + losses (what we divided by)
//   - total: total trades passed in (including open + scratch)
export function computeWinRate(trades) {
  let wins = 0, losses = 0, scratches = 0, openTrades = 0;
  for (const t of (trades || [])) {
    const c = classifyTrade(t);
    if (c === "win") wins++;
    else if (c === "loss") losses++;
    else if (c === "scratch") scratches++;
    else if (c === "open") openTrades++;
  }
  const denominator = wins + losses;
  return {
    winRate: denominator > 0 ? wins / denominator : null,
    wins, losses, scratches, openTrades,
    denominator,
    total: (trades || []).length,
  };
}

// Net contribution of a single trade to the account balance.
// Legacy alias kept for callers that import this name. Identical to tradeNet.
function tradeNetLegacy(t) {
  return tradeNet(t);
}

export function buildAnalytics(trades) {
  if (!trades.length) return null;

  // Use canonical classifier instead of reading t.result directly.
  // This fixes the gross-vs-net mismatch where t.result was set from
  // gross P&L at CSV import time, ignoring fees/commissions.
  const classified = trades.map(t => ({ trade: t, klass: classifyTrade(t) }));
  const wins   = classified.filter(c => c.klass === "win").map(c => c.trade);
  const losses = classified.filter(c => c.klass === "loss").map(c => c.trade);

  const totalPnl = trades.reduce((s, t) => s + (t.pnl || 0), 0);
  const totalFees = trades.reduce((s, t) => s + (Number(t.fees) || 0), 0);
  const totalCommissions = trades.reduce((s, t) => s + (Number(t.commissions) || 0), 0);
  const netPnl = totalPnl - totalFees - totalCommissions;

  // Canonical win rate (excludes scratches & open from denominator).
  const wr = computeWinRate(trades);
  const winRate = wr.winRate ?? 0;  // legacy callers expect a number, not null

  // Avg win/loss based on NET (so it matches what actually hit the account).
  const avgWin  = wins.length   ? wins.reduce((s, t) => s + tradeNet(t), 0) / wins.length     : 0;
  const avgLoss = losses.length ? Math.abs(losses.reduce((s, t) => s + tradeNet(t), 0) / losses.length) : 0;
  const profitFactor = avgLoss > 0 ? (avgWin * wins.length) / (avgLoss * losses.length) : 0;

  // Equity curve — uses NET so the line tracks the actual account balance.
  // `date` is "MM-DD" (year-stripped) for x-axis display/grouping, matching
  // existing consumers. `fullDate` is the true ISO date ("YYYY-MM-DD") and
  // MUST be used for any chronological comparison (e.g. payout-as-of-date
  // lookups) — MM-DD alone sorts/compares incorrectly across a year boundary
  // (e.g. "02-28" < "11-28" lexicographically, even though Feb 2026 is
  // chronologically after Nov 2025 for an account spanning both years).
  const sorted = [...trades].sort((a, b) => new Date(a.trade_date + " " + (a.entry_time || a.trade_time || "00:00")) - new Date(b.trade_date + " " + (b.entry_time || b.trade_time || "00:00")));
  let running = 0;
  const equityCurve = sorted.map((t, i) => { running += tradeNet(t); return { date: t.trade_date?.slice(5), fullDate: t.trade_date, v: Math.round(running), idx: i }; });

  // Drawdown — uses the same NET equity curve
  let peak = 0;
  const drawdown = equityCurve.map(p => { peak = Math.max(peak, p.v); return { date: p.date, v: Math.min(0, p.v - peak) }; });

  // By setup — uses classifier (net) for win count, gross for P&L attribution
  const bySetup = {};
  trades.forEach(t => {
    const s = t.setup_type || "Unknown";
    if (!bySetup[s]) bySetup[s] = { wins: 0, total: 0, pnl: 0 };
    bySetup[s].total++;
    if (classifyTrade(t) === "win") bySetup[s].wins++;
    bySetup[s].pnl += t.pnl || 0;
  });
  const setupData = Object.entries(bySetup).map(([setup, d]) => ({ setup, winRate: Math.round(d.wins / d.total * 100), total: d.total, pnl: Math.round(d.pnl) }));

  // Time-of-day heatmap (30-min buckets) — uses classifier for wins/losses
  const hourBuckets = {};
  trades.forEach(t => {
    const tm = t.entry_time || t.trade_time;
    if (!tm) return;
    const [h, m] = tm.split(":").map(Number);
    const slot = `${h}:${m < 30 ? "00" : "30"}`;
    if (!hourBuckets[slot]) hourBuckets[slot] = { wins: 0, losses: 0, pnl: 0 };
    const k = classifyTrade(t);
    if (k === "win") hourBuckets[slot].wins++;
    else if (k === "loss") hourBuckets[slot].losses++;
    hourBuckets[slot].pnl += t.pnl || 0;
  });
  const heatmap = [...Object.entries(hourBuckets)].sort(([a], [b]) => a.localeCompare(b))
    .map(([slot, d]) => ({ slot, ...d, total: d.wins + d.losses, wr: d.wins / (d.wins + d.losses || 1) }));

  // Streak — uses classifier
  let streak = 0, maxStreak = 0, curSign = null;
  for (const t of sorted) {
    const k = classifyTrade(t);
    if (k === "scratch" || k === "open") continue;  // skip non-decisive
    const s = k === "win" ? 1 : -1;
    if (s === curSign) { streak++; if (s === 1) maxStreak = Math.max(maxStreak, streak); }
    else { streak = 1; curSign = s; }
  }
  // Current streak: walk back through decisive trades from the end
  const decisiveSorted = sorted.filter(t => {
    const k = classifyTrade(t);
    return k === "win" || k === "loss";
  });
  const lastResult = decisiveSorted.length > 0
    ? (classifyTrade(decisiveSorted[decisiveSorted.length - 1]) === "win" ? "Win" : "Loss")
    : null;
  let curStreak = 0;
  for (let i = decisiveSorted.length - 1; i >= 0; i--) {
    const k = classifyTrade(decisiveSorted[i]);
    const isMatch = (lastResult === "Win" && k === "win") || (lastResult === "Loss" && k === "loss");
    if (isMatch) curStreak++;
    else break;
  }

  // Daily P&L — uses NET so the daily-bar values match what hits the account
  const byDay = {};
  trades.forEach(t => {
    const d = t.trade_date || "Unknown";
    if (!byDay[d]) byDay[d] = 0;
    byDay[d] += tradeNet(t);
  });
  const dailyPnl = [...Object.entries(byDay)].sort().map(([d, v]) => ({ d: d.slice(5), v: Math.round(v) }));

  return {
    totalPnl,              // GROSS — sum of broker PnL column
    netPnl,                // NET — what actually moved the balance
    totalFees,
    totalCommissions,
    winRate, avgWin, avgLoss, profitFactor,
    // Also expose the full breakdown for callers that want it
    winRateBreakdown: wr,
    equityCurve, drawdown, setupData, heatmap,
    wins: wins.length, losses: losses.length, total: trades.length,
    maxStreak, curStreak, lastResult, dailyPnl
  };
}
